import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import { ROUTES } from '../routes.ts';
import { resolveUserRole } from '@/auth.ts';
import { consumeReturnTo } from '@/utils';

/**
 * Landing page for the OAuth redirect from Cognito.
 * Amplify processes the authorization code in the background; we listen for
 * the hub event and then navigate to the appropriate home once sign-in is complete.
 * Doctors land on the doctor dashboard; patients land on the home dashboard.
 *
 * If Cognito returns an error in the query string (e.g. invalid_request),
 * we redirect straight to /login so the user is never stuck on this page.
 */
export function Callback() {
  const navigate = useNavigate();

  useEffect(() => {
    // Cognito can return errors as query params before Amplify is even involved,
    // e.g. ?error=invalid_request&error_description=...
    const params = new URLSearchParams(window.location.search);
    const cognitoError = params.get('error');
    if (cognitoError) {
      const description = params.get('error_description') ?? undefined;
      navigate(ROUTES.login, { replace: true, state: { authError: description ?? cognitoError } });
      return;
    }

    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        // Resolving the role first also waits for Amplify to finish settling the new session;
        // navigating straight to a guarded route before that can bounce back to /login.
        const role = await resolveUserRole();
        const returnTo = consumeReturnTo();
        navigate(returnTo ?? (role === 'doctor' ? ROUTES.doctorDashboard : ROUTES.practices), { replace: true });
      }
      if (payload.event === 'signInWithRedirect_failure') {
        const message = (payload.data as { message?: string } | undefined)?.message;
        navigate(ROUTES.login, { replace: true, state: { authError: message ?? 'Aanmelden mislukt.' } });
      }
    });

    return unsubscribe;
  }, [navigate]);

  return <p className="text-muted status-message">Signing in&hellip;</p>;
}
